import { useEffect } from 'react';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { logErrors } from 'utils';
import { getFromBrandConfig } from 'brand-config';
import { selectAccessToken } from 'services/session/sessionSelectors';
import { getUserIdFromAccessToken } from 'services/session/utils';
import { fetchUserDetails } from 'services/user/userAsync';
import { selectUserId } from 'services/user/userSelectors';
import {
  selectOfferPlanCode,
  selectOfferGroupCode,
  selectIsOfferDetailsExist,
} from 'services/offer/offerSelectors';
import { getOfferDetails } from 'services/offer/offerAsync';
import {
  selectIsConversionEligibilityDetailsExist,
  selectIsBillingAccountDetailsExist,
  selectIsAvailableOffersFetched,
  selectIsEligibilityDetailsExist,
} from 'services/ctp/ctpSelectors';
import {
  fetchConversionEligibility,
  fetchAvailableOffers,
  fetchBillingAccountInfo,
  fetchEligibility,
} from 'services/ctp/ctpAsync';
import {
  selectIsCompanyDetailsExist,
  selectBrandVaultProject,
} from 'services/brand/brandSelectors';
import { fetchCompanyCodeDetails } from 'services/brand/brandAsync';
import { findPlanDetails } from 'services/plan/planAsync';
import { selectIsPlanDetailsExist } from 'services/plan/planSelectors';
import { initWorker } from 'services/vault/sdkFunctions';

function useSessionInitialSetup() {
  const dispatch = useAppDispatch();

  const userId = useAppSelector(selectUserId);
  const userAccessToken = useAppSelector(selectAccessToken);
  const vaultProject = useAppSelector(selectBrandVaultProject);
  const isOfferDetailsExist = useAppSelector(selectIsOfferDetailsExist);
  const isConversionEligibilityDetailsExist = useAppSelector(
    selectIsConversionEligibilityDetailsExist,
  );
  const isBillingAccountDetailsExist = useAppSelector(
    selectIsBillingAccountDetailsExist,
  );
  const isEligibilityDetailsExist = useAppSelector(
    selectIsEligibilityDetailsExist,
  );
  const isAvailableOffersFetched = useAppSelector(
    selectIsAvailableOffersFetched,
  );
  const offerPlanCode = useAppSelector(selectOfferPlanCode);
  const groupCode = useAppSelector(selectOfferGroupCode);
  const isCompanyDetailsExist = useAppSelector(selectIsCompanyDetailsExist);
  const isPlanDetailsExist = useAppSelector(selectIsPlanDetailsExist);

  const hasCustomerServiceSupport = getFromBrandConfig(
    'hasCustomerServiceSupport',
  );

  /* Fetch user details if not exist */
  useEffect(() => {
    async function getUserDetails(token: string) {
      try {
        const identifier = getUserIdFromAccessToken(token);
        await dispatch(fetchUserDetails(identifier));
      } catch (error) {
        logErrors(error);
      }
    }

    if (userAccessToken && !userId) {
      getUserDetails(userAccessToken);
    }
  }, [userAccessToken, userId]);

  /* Fetch user offer details if not exist */
  useEffect(() => {
    async function getUserOfferDetails() {
      try {
        await dispatch(getOfferDetails());
      } catch (error) {
        logErrors(error);
      }
    }
    if (!isOfferDetailsExist && userId) {
      getUserOfferDetails();
    }
  }, [userId, isOfferDetailsExist]);

  /* Fetch user conversion elgibilty details if not exist */
  useEffect(() => {
    async function getConversionEligibility() {
      try {
        await dispatch(fetchConversionEligibility());
      } catch (error) {
        logErrors(error);
      }
    }

    if (!isConversionEligibilityDetailsExist && userId) {
      getConversionEligibility();
    }
  }, [userId, isConversionEligibilityDetailsExist]);

  /* Fetch user billing account details if not exist */
  useEffect(() => {
    async function getUserBillingAccountDetails() {
      try {
        await dispatch(fetchBillingAccountInfo());
      } catch (error) {
        logErrors(error);
      }
    }

    if (!isBillingAccountDetailsExist && userId) {
      getUserBillingAccountDetails();
    }
  }, [userId, isBillingAccountDetailsExist]);

  /* Fetch product-change eligibility if not exist (drives WGS upsell banner) */
  useEffect(() => {
    async function getEligibility() {
      try {
        await dispatch(fetchEligibility());
      } catch (error) {
        logErrors(error);
      }
    }

    if (!isEligibilityDetailsExist && userId) {
      getEligibility();
    }
  }, [userId, isEligibilityDetailsExist]);

  /* Fetch available offer details if not exist */
  useEffect(() => {
    async function getAvailableOffers() {
      try {
        await dispatch(fetchAvailableOffers());
      } catch (error) {
        logErrors(error);
      }
    }

    if (!isAvailableOffersFetched && userId) {
      getAvailableOffers();
    }
  }, [userId, isAvailableOffersFetched]);

  /* Fetch the user company details if not exist */
  useEffect(() => {
    async function getCompanyDetails(companyCode: string) {
      try {
        await dispatch(fetchCompanyCodeDetails(companyCode));
      } catch (error) {
        logErrors(error);
      }
    }

    if (hasCustomerServiceSupport && !isCompanyDetailsExist && groupCode) {
      getCompanyDetails(groupCode);
    }
  }, [groupCode, isCompanyDetailsExist, hasCustomerServiceSupport]);

  /* Fetch the user plan details if not exist */
  useEffect(() => {
    async function getPlanDetails(planCode: string) {
      try {
        await dispatch(findPlanDetails(planCode));
      } catch (error) {
        logErrors(error);
      }
    }

    if (!isPlanDetailsExist && offerPlanCode) {
      getPlanDetails(offerPlanCode);
    }
  }, [offerPlanCode, isPlanDetailsExist]);

  // (Dashboard summary fetch removed — DashboardSummaryContext now owns it
  // and shares the single result between the sidebar icon and the alerts
  // card. See src/contexts/DashboardSummaryContext.tsx.)

  /* Init vault worker */
  useEffect(() => {
    const terminate =
      vaultProject && userId
        ? initWorker({ project: vaultProject, userId })
        : () => null;

    return terminate;
  }, [vaultProject, userId]);
}

export default useSessionInitialSetup;
